import { createAsyncThunk } from '@reduxjs/toolkit';
import myPeeguAxios from '../../../../utils/myPeeguAxios';
import { apiHeaders } from '../../../../utils/apiConstants';

const silentRequestHeaders = { [apiHeaders.preventDefaultLoader]: true };

/** AI IEP calls often exceed the default 60s axios timeout */
const AI_IEP_TIMEOUT_MS = 180000;

function formatIepError(error, fallback) {
    if (error?.code === 'ECONNABORTED' || /timeout/i.test(String(error?.message || ''))) {
        return 'This is taking a little longer than usual. Please wait a moment and try again.';
    }
    const payload = error?.response?.data;
    const raw = typeof payload === 'string'
        ? payload
        : (payload?.message || payload?.error || error?.message || fallback);
    const text = String(raw || fallback || '');

    if (/baseline assessment not found|baseline.*incomplete|complete Baseline/i.test(text)) {
        return 'Baseline is not complete for this student yet. Please finish Baseline 1 (or Self Assessment) first, then try again.';
    }
    if (/finalized and cannot be regenerated|iep is finalized|cannot be regenerated until|only mypeegu experts/i.test(text)) {
        return text.includes('Experts')
            ? text
            : 'This IEP is finalized and cannot be regenerated until a MyPeegu Expert unfreezes it.';
    }
    if (/student not found/i.test(text)) {
        return 'We could not find this student. Please go back and open the IEP again from the student list.';
    }
    if (/gemini|quota|429|resource exhausted|billing|ai assist|ai service|technical team|google|generativelanguage|no longer available|internal server/i.test(text)) {
        return 'MyPeegu could not finish this right now. Please wait a moment and try again.';
    }
    if (/Internal Server Error/i.test(text)) {
        return 'MyPeegu could not finish this right now. Please wait a moment and try again.';
    }
    return text || fallback || 'MyPeegu could not finish this right now. Please wait a moment and try again.';
}

export const fetchStudent360Data = createAsyncThunk(
    'iep360/fetchStudent360Data',
    async ({ studentId, academicYear }, { rejectWithValue }) => {
        try {
            const response = await myPeeguAxios.post('/resources/v1/iep-student-360',
                { studentId, academicYear },
                { headers: silentRequestHeaders },
            );
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const uploadIEPEvaluationPDF = createAsyncThunk(
    'iep360/uploadIEPEvaluationPDF',
    async ({ studentId, formData }, { rejectWithValue }) => {
        try {
            const response = await myPeeguAxios.post(`/resources/v1/iep-ai-evaluate/${studentId}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    ...silentRequestHeaders,
                },
                timeout: AI_IEP_TIMEOUT_MS,
            });
            return response.data.data;
        } catch (error) {
            return rejectWithValue(formatIepError(error, 'PDF evaluation failed'));
        }
    },
);

export const generateIEPWithAI = createAsyncThunk(
    'iep360/generateAI',
    async ({ studentId, academicYear, evaluationDetails, clinicalEvaluationReviewed }, { rejectWithValue }) => {
        try {
            const response = await myPeeguAxios.post('/resources/v1/generate-iep-ai',
                { studentId, academicYear, evaluationDetails, clinicalEvaluationReviewed },
                { headers: silentRequestHeaders, timeout: AI_IEP_TIMEOUT_MS },
            );
            return {
                ...(response.data.data || {}),
                _usedFallback: response.data.usedFallback === true,
                _generationMessage: response.data.message || '',
            };
        } catch (error) {
            return rejectWithValue(formatIepError(error, 'Generation failed'));
        }
    },
);

export const generateAccommodationPlanAI = createAsyncThunk(
    'iep360/generateAccommodationPlan',
    async ({ studentId, academicYear }, { rejectWithValue }) => {
        try {
            const response = await myPeeguAxios.post('/resources/v1/generate-accommodation-plan-ai',
                { studentId, academicYear },
                { headers: silentRequestHeaders, timeout: AI_IEP_TIMEOUT_MS },
            );
            return {
                data: response.data.data,
                message: response.data.message,
                usedFallback: response.data.usedFallback,
            };
        } catch (error) {
            return rejectWithValue(formatIepError(error, 'Accommodation plan generation failed'));
        }
    },
);

export const generateSessionSubjectPlanAI = createAsyncThunk(
    'iep360/generateSessionSubjectPlan',
    async ({ studentId, academicYear }, { rejectWithValue }) => {
        try {
            const response = await myPeeguAxios.post('/resources/v1/generate-session-subject-plan-ai',
                { studentId, academicYear },
                { headers: silentRequestHeaders, timeout: AI_IEP_TIMEOUT_MS },
            );
            return {
                data: response.data.data,
                message: response.data.message,
                usedFallback: response.data.usedFallback,
            };
        } catch (error) {
            return rejectWithValue(formatIepError(error, 'Session plan generation failed'));
        }
    },
);

export const saveIEPData = createAsyncThunk(
    'iep360/saveIEPData',
    async (payload, { rejectWithValue }) => {
        try {
            const { studentId, ...dataToSave } = payload;
            const response = await myPeeguAxios.post(`/resources/v1/save-iep-data/${studentId}`, dataToSave);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Save failed');
        }
    },
);
